import type { DiaSemana, HorarioDoDiaType, TipoAtendimento } from './enums';
import { minutosDoHorario } from '../horario';
import { horariosOcupados } from './ocupacao';
import type { PainelDadosOutputType } from './painel.dto';

export type AgendaSlotOutput = {
  horarioId: string;
  diaSemana: DiaSemana;
  horario: HorarioDoDiaType;
  matriculaId: string;
  alunoId: string;
  alunoNome: string;
  alunoConnect: boolean;
  alunoZonaVermelha: boolean;
  professorId: string;
  professorNome: string;
  professorCorAgenda: string;
  materiaId: string;
  estagio: string | null;
  tipoAtendimento: TipoAtendimento;
};
export type AgendaSlotOutputType = AgendaSlotOutput;

const ORDEM_DIAS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

/** Função pura sobre o snapshot de `GET /painel`; `professorId`/`alunoId` são filtros opcionais. */
export function derivarAgendaSlots(
  dados: PainelDadosOutputType,
  filtro: { professorId?: string; alunoId?: string } = {},
): AgendaSlotOutputType[] {
  const alunoPorId = new Map(dados.alunos.map((aluno) => [aluno.id, aluno]));
  const professorPorId = new Map(dados.professores.map((professor) => [professor.id, professor]));

  const matriculas = dados.matriculas.filter(
    (matricula) =>
      matricula.situacao === 'ATIVA' &&
      (!filtro.professorId || matricula.professorId === filtro.professorId) &&
      (!filtro.alunoId || matricula.alunoId === filtro.alunoId),
  );

  return matriculas
    .flatMap((matricula) => {
      const aluno = alunoPorId.get(matricula.alunoId);
      const professor = professorPorId.get(matricula.professorId);

      return matricula.horarios.map((horario) => ({
        horarioId: horario.id,
        diaSemana: horario.diaSemana,
        horario: horario.horario,
        matriculaId: matricula.id,
        alunoId: matricula.alunoId,
        alunoNome: aluno?.nome ?? '',
        alunoConnect: aluno?.connect ?? false,
        alunoZonaVermelha: aluno?.zonaVermelha ?? false,
        professorId: matricula.professorId,
        professorNome: professor?.nome ?? '',
        professorCorAgenda: professor?.corAgenda ?? '#64748b',
        materiaId: matricula.materiaId,
        estagio: matricula.estagio,
        tipoAtendimento: matricula.tipoAtendimento,
      }));
    })
    .sort(
      (a, b) =>
        ORDEM_DIAS.indexOf(a.diaSemana) - ORDEM_DIAS.indexOf(b.diaSemana) ||
        a.horario.localeCompare(b.horario),
    );
}

export interface OcupacaoCelula {
  /** Slots que realmente comecam nesse horario -- as pills que a celula renderiza. */
  ocupantes: AgendaSlotOutputType[];
  /** Slots de um horário anterior cuja aula ainda "vaza" pra esse (ex.: `REGULAR` às 14:00 na célula de 14:30): só conta pra ocupação/cor. */
  overflow: AgendaSlotOutputType[];
  /** `capacidadePorHorario` do professor da celula, ou 0 se o professor nao existir mais. */
  capacidade: number;
}

/** Ocupação de uma célula (professor x dia x horário), com o spillover de `REGULAR` do horário anterior (mesma regra de `horariosOcupados`). */
export function calcularOcupacaoCelula(
  dados: PainelDadosOutputType,
  { professorId, diaSemana, horario }: { professorId: string; diaSemana: string; horario: string },
): OcupacaoCelula {
  const professor = dados.professores.find((p) => p.id === professorId);
  const slotsDoProfessorNoDia = derivarAgendaSlots(dados, { professorId }).filter(
    (slot) => slot.diaSemana === diaSemana,
  );

  const ocupantes = slotsDoProfessorNoDia.filter((slot) => slot.horario === horario);
  const overflow = slotsDoProfessorNoDia.filter(
    (slot) =>
      slot.horario !== horario &&
      horariosOcupados(slot.horario, slot.tipoAtendimento).includes(horario),
  );

  return { ocupantes, overflow, capacidade: professor?.capacidadePorHorario ?? 0 };
}

/**
 * Só soma quem atende nesse dia/horário (senão haveria capacidade fantasma). Exportada: `schedule-grid.tsx` usa a
 * mesma checagem no seletor de professor — disponibilidade é validada pelo servidor, capacidade é só indicativa.
 */
export function professorDisponivel(
  professor: { diasDisponiveis: string[]; horarioInicial: string; horarioFinal: string },
  diaSemana: string,
  horario: string,
): boolean {
  const minutosAlvo = minutosDoHorario(horario);
  return (
    professor.diasDisponiveis.includes(diaSemana) &&
    minutosAlvo >= minutosDoHorario(professor.horarioInicial) &&
    minutosAlvo < minutosDoHorario(professor.horarioFinal)
  );
}

/**
 * Soma os professores disponíveis no horário (ou só `professorIds`, ex.: pool de uma matéria). Ao contrário de
 * `calcularOcupacaoCelula`, a capacidade É somada: cada professor supervisiona seu próprio grupo.
 */
export function calcularOcupacaoUnidadeCelula(
  dados: PainelDadosOutputType,
  {
    diaSemana,
    horario,
    professorIds,
  }: { diaSemana: string; horario: string; professorIds?: string[] },
): OcupacaoCelula {
  const pool = professorIds
    ? dados.professores.filter((p) => professorIds.includes(p.id))
    : dados.professores;
  const disponiveis = pool.filter((p) => professorDisponivel(p, diaSemana, horario));

  const porProfessor = disponiveis.map((p) =>
    calcularOcupacaoCelula(dados, { professorId: p.id, diaSemana, horario }),
  );

  return {
    ocupantes: porProfessor.flatMap((o) => o.ocupantes),
    overflow: porProfessor.flatMap((o) => o.overflow),
    capacidade: porProfessor.reduce((soma, o) => soma + o.capacidade, 0),
  };
}
